import { logger } from '../Util/Logger/ClientLogger';

const LogTitle = 'AudioAnalysisService';

export interface AudioLevels {
  microphone: number;
  phone: number;
  combined: number;
}

export interface AudioAnalysisConfig {
  fftSize?: number;
  smoothingTimeConstant?: number;
  minDecibels?: number;
  maxDecibels?: number;
  updateInterval?: number;
}

class AudioAnalysisService {
  private _audioContext: AudioContext | null = null;
  private _microphoneAnalyser: AnalyserNode | null = null;
  private _phoneAnalyser: AnalyserNode | null = null;
  private _microphoneSource: MediaStreamAudioSourceNode | null = null;
  private _phoneSource: MediaStreamAudioSourceNode | null = null;
  private _microphoneStream: MediaStream | null = null;
  private _phoneStream: MediaStream | null = null;
  private _isActive: boolean = false;
  private _intervalId: number | null = null;
  private _updateInterval: number = 32;
  private _config: AudioAnalysisConfig = {
    fftSize: 128,
    smoothingTimeConstant: 0.8,
    minDecibels: -90,
    maxDecibels: -10,
    updateInterval: 32
  };

  private _callbacks: Set<(levels: AudioLevels) => void> = new Set();
  private _frequencyData: Uint8Array | null = null;

  constructor(config?: AudioAnalysisConfig) {
    if (config) {
      this._config = { ...this._config, ...config };
    }
    this._updateInterval = this._config.updateInterval || 32;
  }

  /**
   * Initialize the audio analysis service
   */
  initialize(audioContext: AudioContext): void {
    if (this._isActive) {
      logger.warn(`${LogTitle} Already initialized`);
      return;
    }

    this._audioContext = audioContext;
    this._isActive = true;
    
    logger.info(`${LogTitle} Initialized with audio context`);
  }

  /**
   * Start analyzing microphone audio stream
   */
  startMicrophoneAnalysis(mediaStream: MediaStream): void {
    if (!this._audioContext || !this._isActive) {
      logger.error(`${LogTitle} Not initialized`);
      return;
    }

    try {
      // Store the stream for cleanup later
      this._microphoneStream = mediaStream;
      
      // Create microphone source and analyser
      this._microphoneSource = this._audioContext.createMediaStreamSource(mediaStream);
      this._microphoneAnalyser = this._audioContext.createAnalyser();
      
      // Configure analyser
      this._microphoneAnalyser.fftSize = this._config.fftSize!;
      this._microphoneAnalyser.smoothingTimeConstant = this._config.smoothingTimeConstant!;
      this._microphoneAnalyser.minDecibels = this._config.minDecibels!;
      this._microphoneAnalyser.maxDecibels = this._config.maxDecibels!;

      // Connect microphone to analyser
      this._microphoneSource.connect(this._microphoneAnalyser);
      
      logger.info(`${LogTitle} Microphone analysis started`);
    } catch (error) {
      logger.error(`${LogTitle} Failed to start microphone analysis:`, LogTitle, { error: String(error) });
    }
  }

  /**
   * Start analyzing phone audio stream
   */
  startPhoneAnalysis(mediaStream: MediaStream): void {
    if (!this._audioContext || !this._isActive) {
      logger.error(`${LogTitle} Not initialized`);
      return;
    }

    try {
      // Store the stream for cleanup later
      this._phoneStream = mediaStream;
      
      // Create phone source and analyser
      this._phoneSource = this._audioContext.createMediaStreamSource(mediaStream);
      this._phoneAnalyser = this._audioContext.createAnalyser();
      
      // Configure analyser
      this._phoneAnalyser.fftSize = this._config.fftSize!;
      this._phoneAnalyser.smoothingTimeConstant = this._config.smoothingTimeConstant!;
      this._phoneAnalyser.minDecibels = this._config.minDecibels!;
      this._phoneAnalyser.maxDecibels = this._config.maxDecibels!;

      // Connect phone to analyser
      this._phoneSource.connect(this._phoneAnalyser);
      
      logger.info(`${LogTitle} Phone analysis started`);
    } catch (error) {
      logger.error(`${LogTitle} Failed to start phone analysis:`, LogTitle, { error: String(error) });
    }
  }

  /**
   * Subscribe to audio level updates
   */
  subscribe(callback: (levels: AudioLevels) => void): () => void {
    this._callbacks.add(callback);
    
    // Start analysis loop if not already running
    if (this._callbacks.size === 1) {
      this._startAnalysisLoop();
    }

    // Return unsubscribe function
    return () => {
      this._callbacks.delete(callback);
      if (this._callbacks.size === 0) {
        this._stopAnalysisLoop();
      }
    };
  }

  /**
   * Get current audio levels synchronously
   */
  getCurrentLevels(): AudioLevels {
    const microphone = this._getMicrophoneLevel();
    const phone = this._getPhoneLevel();
    const combined = Math.max(microphone, phone);

    return {
      microphone,
      phone,
      combined
    };
  }

  /**
   * Get frequency data for visualization
   * Combines both microphone and phone frequency data to show animation for both agent and customer voices
   */
  getFrequencyData(): Uint8Array | null {
    if (!this._microphoneAnalyser && !this._phoneAnalyser) {
      return null;
    }

    const bufferLength = this._config.fftSize! / 2;
    if (!this._frequencyData || this._frequencyData.length !== bufferLength) {
      this._frequencyData = new Uint8Array(new ArrayBuffer(bufferLength));
    }

    // If we have both analysers, combine their frequency data
    if (this._microphoneAnalyser && this._phoneAnalyser) {
      const micData = new Uint8Array(bufferLength);
      const phoneData = new Uint8Array(bufferLength);
      
      this._microphoneAnalyser.getByteFrequencyData(micData);
      this._phoneAnalyser.getByteFrequencyData(phoneData);
      
      // Combine by taking the maximum value at each frequency bin
      // This ensures animation shows when either agent OR customer speaks
      for (let i = 0; i < bufferLength; i++) {
        this._frequencyData[i] = Math.max(micData[i], phoneData[i]);
      }
    } 
    // If only one analyser is available, use that
    else if (this._microphoneAnalyser) {
      this._microphoneAnalyser.getByteFrequencyData(this._frequencyData);
    } 
    else if (this._phoneAnalyser) {
      this._phoneAnalyser.getByteFrequencyData(this._frequencyData);
    }

    return this._frequencyData;
  }

  /**
   * Stop all analysis and cleanup
   */
  stop(): void {
    this._stopAnalysisLoop();
    
    // Disconnect and cleanup microphone
    if (this._microphoneSource) {
      this._microphoneSource.disconnect();
      this._microphoneSource = null;
    }
    
    // Stop microphone stream tracks to release the microphone
    if (this._microphoneStream) {
      this._microphoneStream.getTracks().forEach(track => {
        track.stop();
        logger.info(`${LogTitle} Stopped microphone track: ${track.kind}`);
      });
      this._microphoneStream = null;
    }
    
    // Disconnect and cleanup phone
    if (this._phoneSource) {
      this._phoneSource.disconnect();
      this._phoneSource = null;
    }
    
    // Note: We don't stop phone stream tracks as they're managed by Twilio
    this._phoneStream = null;

    this._microphoneAnalyser = null;
    this._phoneAnalyser = null;
    this._isActive = false;
    
    logger.info(`${LogTitle} Stopped and released all media resources`);
  }

  private _startAnalysisLoop(): void {
    if (this._intervalId) {
      return;
    }

    const analyze = () => {
      if (!this._isActive) {
        return;
      }

      const levels = this.getCurrentLevels();
      
      // Notify all subscribers
      this._callbacks.forEach(callback => {
        try {
          callback(levels);
        } catch (error) {
          logger.error(`${LogTitle} Callback error:`, LogTitle, { error: String(error) });
        }
      });
    };

    this._intervalId = window.setInterval(analyze, this._updateInterval);
  }

  private _stopAnalysisLoop(): void {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  private _getMicrophoneLevel(): number {
    if (!this._microphoneAnalyser) {
      return 0;
    }

    const bufferLength = this._microphoneAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this._microphoneAnalyser.getByteFrequencyData(dataArray);

    // Calculate RMS (Root Mean Square) for volume level
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    
    return Math.sqrt(sum / bufferLength) / 255;
  }

  private _getPhoneLevel(): number {
    if (!this._phoneAnalyser) {
      return 0;
    }

    const bufferLength = this._phoneAnalyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    this._phoneAnalyser.getByteFrequencyData(dataArray);

    // Calculate RMS (Root Mean Square) for volume level
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    
    return Math.sqrt(sum / bufferLength) / 255;
  }
}

export default AudioAnalysisService;
